import { fmtDate } from '../../utils/datetime';
import React, { useState, useEffect } from "react";

import { toast } from '../../utils/toast';
import * as Yup from "yup";
import { useFormik } from "formik";
import { useLoadScript } from "@react-google-maps/api";

import { Users, Plus, Edit3, Trash2, X, User } from "lucide-react";
import PlacesAutocompleteInput from "./utils/PlacesAutocompleteInput";
import config from "../../config";
import { createNewCountry, deleteCountry, editCountry, getAllCountries } from "../../api/master";
import { useAuth } from "../../context/AuthContext";
const libraries: any = ["places"];

interface Country {
  id: number;
  tenant_id: number;
  name: string;
  latitude: string;
  longitude: string;
  created_at: string;
  updated_at?: string;
}
// Move Modal component OUTSIDE of Master component
const Modal = ({
  isOpen,
  onClose,
  title,
  children,
}: {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
}) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="w-full max-w-md p-6 mx-4 bg-white rounded-lg">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
};

const MyCountry: React.FC = () => {
  const googleMapsApiKey: string = config.googleMapsApiKey;
  const [countries, setCountries] = useState([]);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [currentCountryId, setCurrentCountryId] = useState(0);

  const token = localStorage.getItem("authToken");
  const { user } = useAuth();
  const [lat, setLat] = useState("");
  const [lng, setLng] = useState("");
  const [placeName, setPlacename] = useState("");
  const { isLoaded } = useLoadScript({
    googleMapsApiKey: googleMapsApiKey,
    libraries: libraries,
  });
  useEffect(() => {
    if (user && user.tenant_id) {
      getCountries();
    }
  }, [user]);

  /**Add New Country */
  const initialValues = {
    name: "",
  };

  const CountrySchema = Yup.object({
    name: Yup.string().required("Please enter Country Name"),
  });

  const {
    setValues,
    values,
    handleBlur,
    handleChange,
    handleSubmit,
    errors,
    touched,
  } = useFormik({
    initialValues,
    validationSchema: CountrySchema,
    onSubmit: async (values) => {
      if (!isEdit) {
        try {
          const response = await createNewCountry(values.name, user.tenant_id, lat, lng)

          if (response.status === 200) {
            getCountries();
            setIsAddModalOpen(false);
            setValues({ name: "" });
            toast.success("Country Created Successfully!");
          }
        } catch (error: any) {
          toast.error(error.message || "Something went wrong.");
        }
      }

      if (isEdit) {
        try {
          const response = await editCountry(values.name, user.tenant_id, lat, lng, currentCountryId)

          if (response.status === 200) {
            getCountries();
            setValues({ name: "" });
            setCurrentCountryId(0);
            setIsEdit(false);
            setIsEditModalOpen(false);
            toast.success("Country Updated Successfully!");
          }
        } catch (error: any) {
          toast.error(error.message || "Something went wrong.");
        }
      }
    },
  });

  const getCountries = async () => {
    try {
      const response = await getAllCountries(user.tenant_id)
      setCountries(response);
    } catch (error) { }
  };

  const handleEdit = (item: Country) => {
    setValues({ name: item.name });
    setCurrentCountryId(item.id);
    setIsEditModalOpen(true);
    setIsEdit(true);
  };

  const handleDelete = async (id: number) => {
    if (!await toast.confirm("This action cannot be undone.", "Delete country?", "Delete")) return;
    try {
      await deleteCountry(id);
      setCountries(countries.filter((country: Country) => country.id !== id));
      toast.success("Country deleted successfully.");
    } catch (error: any) {
      toast.error(error.message || "Failed to delete country.");
    }
  };

  return (
    <div className="p-4 md:p-8">
      <div className="mx-auto max-w-7xl">
        {/* Header */}
        <div className="flex flex-col items-start justify-between gap-4 mb-8 md:flex-row md:items-center">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Country Master</h1>
            <p className="mt-1 text-blue-600">Manage Country</p>
          </div>
          <button
            onClick={() => setIsAddModalOpen(true)}
            className="flex items-center px-4 py-2 text-white transition-colors bg-blue-600 rounded-lg hover:bg-blue-700"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Country
          </button>
        </div>

        {/* Master Data List */}
        <div className="bg-white shadow-md rounded-xl">
          <div className="p-6">
            {/* Header */}
            <h3 className="flex items-center gap-2 mb-6 text-lg font-semibold text-gray-900">
              <User className="w-5 h-5 text-blue-600" />
              Country ({countries.length})
            </h3>

            {/* Table */}
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="px-4 py-3 font-medium text-left text-gray-700">
                      SN
                    </th>

                    <th className="px-4 py-3 font-medium text-left text-gray-700">
                      Name
                    </th>

                    <th className="px-4 py-3 font-medium text-left text-gray-700">
                      Latitude
                    </th>
                    <th className="px-4 py-3 font-medium text-left text-gray-700">
                      Longitude
                    </th>

                    <th className="px-4 py-3 font-medium text-left text-gray-700">
                      Updated At
                    </th>

                    <th className="px-4 py-3 font-medium text-left text-gray-700">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {countries.map((item: Country, index) => (
                    <tr
                      key={item.id}
                      className="transition-colors border-b border-gray-100 hover:bg-blue-50"
                    >
                      <td className="px-4 py-4">
                        <div className="flex items-center">
                          <div className="font-medium text-gray-900">
                            {index + 1}
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-4">
                        <div className="flex items-center">
                          <div className="font-medium text-gray-900">
                            {item.name}
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-4">
                        <div className="flex items-center">
                          <div className="font-medium text-gray-900">
                            {item.latitude}
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-4">
                        <div className="flex items-center">
                          <div className="font-medium text-gray-900">
                            {item.longitude}
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-4">
                        <div className="flex items-center">
                          <div className="font-medium text-gray-900">
                            {fmtDate(item.updated_at)}
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-4">
                        <div className="flex gap-2">
                          <button
                            onClick={() => handleEdit(item)}
                            className="p-2 transition-colors border border-gray-300 rounded hover:bg-gray-100"
                            title="Edit"
                            type="submit"
                          >
                            <Edit3 className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDelete(item.id)}
                            className="p-2 text-white transition-colors bg-red-600 rounded hover:bg-red-700"
                            title="Delete"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Add Modal */}
        <Modal
          isOpen={isAddModalOpen}
          onClose={() => setIsAddModalOpen(false)}
          title={`Add New Country`}
        >
          <div className="space-y-4">
            <form onSubmit={handleSubmit}>
              <div>
                {isLoaded ? (
                  <PlacesAutocompleteInput
                    onPlaceSelect={(name, lat, lng) => {
                      setLat(lat);
                      setLng(lng);
                      setPlacename(name);
                      setValues({ name });
                    }}
                  />
                ) : (
                  <p className="text-sm text-gray-500 mb-3">Loading maps...</p>
                )}
                <label className="block mb-1 text-sm font-medium text-gray-700">
                  Name *
                </label>
                <input
                  name="name"
                  value={values.name}
                  onBlur={handleBlur}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                {errors.name && touched.name && (
                  <p className="text-red-500">{errors.name}</p>
                )}
              </div>

              <div className="flex justify-end pt-4 space-x-3">
                <button className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50">
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Add Country
                </button>
              </div>
            </form>
          </div>
        </Modal>

        {/* Edit Modal */}
        <Modal
          isOpen={isEditModalOpen}
          onClose={() => setIsEditModalOpen(false)}
          title="Edit Country"
        >
          <div className="space-y-4">
            <form onSubmit={handleSubmit}>
              <div>
                {isLoaded ? (
                  <PlacesAutocompleteInput
                    onPlaceSelect={(name, lat, lng) => {
                      setLat(lat);
                      setLng(lng);
                      setPlacename(name);
                      setValues({ name });
                    }}
                  />
                ) : (
                  <p className="text-sm text-gray-500 mb-3">Loading maps...</p>
                )}
                <label className="block mb-1 text-sm font-medium text-gray-700">
                  Name *
                </label>
                <input
                  name="name"
                  value={values.name}
                  onBlur={handleBlur}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                {errors.name && touched.name && (
                  <p className="text-red-500">{errors.name}</p>
                )}
              </div>
              <div className="flex justify-end pt-4 space-x-3">
                <button
                  onClick={() => setIsEditModalOpen(false)}
                  className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button className="px-4 py-2 text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed">
                  Update
                </button>
              </div>
            </form>
          </div>
        </Modal>
      </div>
    </div>
  );
};

export default MyCountry;
